/**
 * ledger-repository.js — writing the Chart of Accounts.
 *
 * Two masters live here: GROUPS (the classification tree) and LEDGERS (the
 * accounts themselves). Both are small, slow-changing collections that every
 * screen holds in memory, so they are read with live listeners and written one
 * at a time — the transaction discipline that the posting engine needs does not
 * apply to a master that nothing increments.
 *
 * With one exception, and it is the interesting one: a ledger's NAME is
 * reserved atomically, because "is this name free" and "take it" must be a
 * single step. See `ledgerNameDoc` in lib/paths for why.
 *
 * NOTHING IS DELETED. A ledger that has been posted to cannot go without taking
 * the postings' meaning with it, so a ledger is ARCHIVED: it disappears from
 * every picker and stays in every report that already names it.
 */

import {
  deleteDoc,
  doc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  groupDoc,
  groupsCol,
  ledgerDoc,
  ledgerNameDoc,
  ledgersCol,
  accEntriesCol,
} from '../../lib/paths';
import { round } from '../../lib/money';
import { ACTIVITY_TYPES } from '../../lib/audit';
import { recordActivity } from '../../lib/activity-log';
import {
  PRIMARY_GROUPS,
  SEED_LEDGERS,
  canReparent,
  indexGroups,
  isPrimaryGroup,
} from '../../lib/ledger-groups';
import { SIDES, validateLedger } from '../../lib/ledgers';

export const COA_SCHEMA_VERSION = 1;

export class ChartError extends Error {
  constructor(message, code, details) {
    super(message);
    this.name = 'ChartError';
    this.code = code;
    this.details = details;
  }
}

/** Two spellings of one head is the bug this normalisation prevents. */
export const nameKey = (name) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

/* ------------------------------------------------------------------ *
 * Seeding
 * ------------------------------------------------------------------ */

/**
 * Write the primary groups and the day-one ledgers.
 *
 * IDEMPOTENT, and deliberately so. It runs at onboarding, and it runs again
 * from Settings for a business created before this module existed — a business
 * whose books are half migrated must be able to re-run it without losing the
 * groups it has already customised. Every write is a merge, and a group the
 * user renamed keeps its name because only the fields the seed OWNS are
 * written: nature, parent, sort order and flags. The name is written only when
 * the document does not exist yet.
 *
 * Firestore caps a batch at 500 writes. The seed is ~45 documents, so one batch
 * is enough and the cap is noted rather than worked around.
 */
/**
 * A Firestore failure, restated as something a person can act on.
 *
 * The raw codes are accurate and useless: "Missing or insufficient permissions"
 * does not tell somebody staring at a dead button whether to deploy rules, sign
 * in again, or ask for a different role.
 */
function describeChartFailure(error) {
  switch (error?.code) {
    case 'permission-denied':
      return new ChartError(
        'The database refused the write. Either the security rules have not been deployed to this project, or your sign-in does not carry the business it belongs to. Sign out and back in first; if that does not help, the rules need deploying.',
        'permission-denied'
      );
    case 'unauthenticated':
      return new ChartError('Your session has expired. Sign in again and retry.', 'unauthenticated');
    case 'unavailable':
    case 'deadline-exceeded':
    case 'timeout':
      return new ChartError(
        'The write never reached the server, so nothing was created. Check the connection to Firestore and try again.',
        'unavailable'
      );
    default:
      return new ChartError(
        error?.message || 'The chart of accounts could not be created.',
        error?.code || 'unknown'
      );
  }
}

/**
 * A promise that will not hang forever.
 *
 * WHY THIS IS HERE, AND IT IS NOT DEFENSIVENESS FOR ITS OWN SAKE.
 *
 * `batch.commit()` resolves when the SERVER acknowledges the write. If it
 * cannot reach the server it does not reject — it queues the write and waits,
 * indefinitely. To the person who pressed the button that is a button that does
 * nothing: no error, no success, the label stuck on "Creating…" until they give
 * up and reload, at which point the queued write goes with the tab.
 *
 * A bounded wait turns a silent hang into a sentence.
 */
function withTimeout(promise, ms, message) {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(message);
      error.code = 'timeout';
      reject(error);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

export async function seedChartOfAccounts({ businessId, force = false }) {
  if (!businessId) {
    throw new ChartError(
      'This sign-in is not linked to a business, so there is nothing to create the chart against. Sign out and back in; if it persists, the business claim has not been minted for this user.',
      'no-business'
    );
  }
  try {
    return await seedChartOfAccountsInner({ businessId, force });
  } catch (error) {
    if (error instanceof ChartError) throw error;
    throw describeChartFailure(error);
  }
}

async function seedChartOfAccountsInner({ businessId, force }) {
  const existingGroups = await getDocs(query(groupsCol(businessId), limit(500)));
  const haveGroup = new Set(existingGroups.docs.map((d) => d.id));
  const existingLedgers = await getDocs(query(ledgersCol(businessId), limit(500)));
  const haveLedger = new Set(existingLedgers.docs.map((d) => d.id));

  const batch = writeBatch(db);
  const now = serverTimestamp();
  let created = 0;

  PRIMARY_GROUPS.forEach((group) => {
    const ref = groupDoc(businessId, group.id);
    const owned = {
      schemaVersion: COA_SCHEMA_VERSION,
      nature: group.nature,
      parentId: group.parentId,
      sortOrder: group.sortOrder,
      flags: group.flags,
      isPrimary: true,
      updatedAt: now,
    };
    if (!haveGroup.has(group.id) || force) {
      batch.set(
        ref,
        {
          ...owned,
          name: group.name,
          description: group.description || '',
          isArchived: false,
          createdAt: now,
        },
        { merge: true }
      );
      created += 1;
    } else {
      /* Already there: refresh only what the seed owns, so a rename survives. */
      batch.set(ref, owned, { merge: true });
    }
  });

  SEED_LEDGERS.forEach((ledger) => {
    if (haveLedger.has(ledger.id) && !force) return;
    batch.set(
      ledgerDoc(businessId, ledger.id),
      {
        schemaVersion: COA_SCHEMA_VERSION,
        name: ledger.name,
        nameKey: nameKey(ledger.name),
        groupId: ledger.groupId,
        openingBalance: 0,
        openingSide: SIDES.DEBIT,
        movement: 0,
        totalDebit: 0,
        totalCredit: 0,
        isSystem: !!ledger.isSystem,
        isArchived: false,
        taxRole: ledger.taxRole || null,
        description: ledger.description || '',
        createdAt: now,
        updatedAt: now,
      },
      { merge: true }
    );
    /* The reservation, so a user cannot later create a second "Cash". */
    batch.set(
      ledgerNameDoc(businessId, nameKey(ledger.name)),
      { ledgerId: ledger.id, updatedAt: now },
      { merge: true }
    );
    created += 1;
  });

  await withTimeout(
    batch.commit(),
    20000,
    'The write did not reach the server within twenty seconds.'
  );
  return { created };
}

/** Whether this business has a chart of accounts yet. */
export async function chartIsSeeded(businessId) {
  if (!businessId) return false;
  const snap = await getDocs(query(groupsCol(businessId), limit(1)));
  return !snap.empty;
}

/* ------------------------------------------------------------------ *
 * Groups
 * ------------------------------------------------------------------ */

export async function createGroup({ businessId, values, actor = null }) {
  if (!businessId) throw new ChartError('Missing business.', 'no-business');
  const name = String(values?.name || '').trim();
  if (!name) throw new ChartError('Give the group a name.', 'invalid');
  if (!values.parentId) {
    throw new ChartError(
      'A new group sits under an existing one. Choose its parent.',
      'invalid'
    );
  }

  const ref = doc(groupsCol(businessId));
  const now = serverTimestamp();

  await setDoc(ref, {
    schemaVersion: COA_SCHEMA_VERSION,
    name,
    parentId: values.parentId,
    /* A sub-group inherits its parent's nature unless one was chosen. Nature is
       the field every report reasons about, so a blank one is never allowed to
       reach the database. */
    nature: values.nature || null,
    flags: Array.isArray(values.flags) ? values.flags : [],
    description: String(values.description || '').slice(0, 240),
    sortOrder: Number.isFinite(values.sortOrder) ? values.sortOrder : 9000,
    isPrimary: false,
    isArchived: false,
    createdAt: now,
    updatedAt: now,
  });

  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.SETTINGS_CHANGED,
    subjectId: ref.id,
    subjectLabel: name,
    detail: 'Chart of accounts: group created',
  });

  return { id: ref.id };
}

export async function updateGroup({ businessId, groupId, values, groups = [], actor = null }) {
  if (!businessId || !groupId) throw new ChartError('Missing group.', 'no-group');

  const patch = { updatedAt: serverTimestamp() };
  if (values.name !== undefined) patch.name = String(values.name).trim();
  if (values.description !== undefined) {
    patch.description = String(values.description || '').slice(0, 240);
  }
  if (values.sortOrder !== undefined) patch.sortOrder = Number(values.sortOrder) || 9000;

  if (values.parentId !== undefined) {
    if (isPrimaryGroup(groupId)) {
      throw new ChartError(
        'A primary group cannot be moved. It is where a standard balance sheet expects to find it.',
        'primary-group'
      );
    }
    if (!canReparent(groupId, values.parentId, indexGroups(groups))) {
      throw new ChartError(
        'That would make the group its own parent, which no report could then read.',
        'cycle'
      );
    }
    patch.parentId = values.parentId;
  }

  if (values.nature !== undefined) {
    if (isPrimaryGroup(groupId)) {
      throw new ChartError(
        'A primary group keeps its nature. Everything already posted under it was classified this way.',
        'primary-group'
      );
    }
    patch.nature = values.nature;
  }

  await updateDoc(groupDoc(businessId, groupId), patch);

  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.SETTINGS_CHANGED,
    subjectId: groupId,
    subjectLabel: patch.name || groupId,
    detail: 'Chart of accounts: group changed',
  });

  return { id: groupId };
}

/**
 * Archive a group.
 *
 * Refused while anything still sits under it, because a hidden parent with
 * visible children is a tree that renders wrong everywhere at once. The caller
 * passes the loaded groups and ledgers rather than this issuing two queries —
 * every screen that offers the action already holds both.
 */
export async function archiveGroup({ businessId, groupId, groups = [], ledgers = [], actor = null }) {
  if (isPrimaryGroup(groupId)) {
    throw new ChartError('A primary group cannot be removed.', 'primary-group');
  }
  const childGroups = groups.filter((g) => g.parentId === groupId && !g.isArchived);
  const childLedgers = ledgers.filter((l) => l.groupId === groupId && !l.isArchived);
  if (childGroups.length || childLedgers.length) {
    throw new ChartError(
      `Move the ${childGroups.length + childLedgers.length} item${
        childGroups.length + childLedgers.length === 1 ? '' : 's'
      } under this group somewhere else first.`,
      'not-empty'
    );
  }

  await updateDoc(groupDoc(businessId, groupId), {
    isArchived: true,
    updatedAt: serverTimestamp(),
  });

  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.SETTINGS_CHANGED,
    subjectId: groupId,
    detail: 'Chart of accounts: group archived',
  });

  return { id: groupId };
}

/* ------------------------------------------------------------------ *
 * Ledgers
 * ------------------------------------------------------------------ */

/**
 * Create a ledger.
 *
 * This is the function behind "+ Create Ledger" inside a voucher, and its
 * whole job is to be fast enough that nobody leaves the voucher to do it. One
 * transaction: take the name, write the ledger. The opening balance, when there
 * is one, is written as a FIELD rather than as a posting — see the note in
 * lib/ledgers on why opening is stored as amount-plus-side.
 *
 * @returns {{id, name}} so the caller can select it into the row it was
 *          created from, which is the entire point of the feature.
 */
export async function createLedger({ businessId, values, existingNames = new Set(), actor = null }) {
  if (!businessId) throw new ChartError('Missing business.', 'no-business');

  const errors = validateLedger(values, { existingNames });
  const first = Object.values(errors)[0];
  if (first) throw new ChartError(first, 'invalid', errors);

  const name = String(values.name).trim();
  const key = nameKey(name);
  const ref = doc(ledgersCol(businessId));
  const reservation = ledgerNameDoc(businessId, key);

  await runTransaction(db, async (tx) => {
    const taken = await tx.get(reservation);
    if (taken.exists()) {
      throw new ChartError(
        `A ledger called “${name}” already exists. Two spellings of one head is a reporting bug that takes a year to notice.`,
        'duplicate-name'
      );
    }

    const now = serverTimestamp();
    const opening = Math.abs(round(values.openingBalance || 0));

    tx.set(ref, {
      schemaVersion: COA_SCHEMA_VERSION,
      name,
      nameKey: key,
      groupId: values.groupId,

      openingBalance: opening,
      openingSide: values.openingSide === SIDES.CREDIT ? SIDES.CREDIT : SIDES.DEBIT,
      /** Everything posted since. Moved with increment(), never rewritten. */
      movement: 0,
      totalDebit: 0,
      totalCredit: 0,

      /* Party fields. Written as null rather than omitted, so a screen can read
         them without checking whether the key exists. */
      gstin: String(values.gstin || '').trim().toUpperCase() || null,
      pan: String(values.pan || '').trim().toUpperCase() || null,
      phone: String(values.phone || '').trim() || null,
      email: String(values.email || '').trim() || null,
      address: values.address || null,
      creditPeriodDays: Number.isFinite(Number(values.creditPeriodDays))
        ? Number(values.creditPeriodDays)
        : null,
      creditLimit: round(values.creditLimit || 0) || null,
      billwise: values.billwise !== false,

      /* Bank fields. */
      accountNumber: String(values.accountNumber || '').trim() || null,
      ifsc: String(values.ifsc || '').trim().toUpperCase() || null,
      bankName: String(values.bankName || '').trim() || null,
      branch: String(values.branch || '').trim() || null,

      taxRole: values.taxRole || null,
      /** The old Parties document this ledger stands for, when migrated. */
      partyId: values.partyId || null,
      /** The old Accounts document this ledger stands for, when migrated. */
      accountId: values.accountId || null,

      notes: String(values.notes || '').slice(0, 500),
      isSystem: false,
      isArchived: false,
      createdAt: now,
      createdBy: actor?.uid || null,
      updatedAt: now,
    });

    tx.set(reservation, { ledgerId: ref.id, updatedAt: now });
  });

  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.SETTINGS_CHANGED,
    subjectId: ref.id,
    subjectLabel: name,
    detail: 'Ledger created',
  });

  return { id: ref.id, name };
}

/**
 * Change a ledger.
 *
 * A rename moves the reservation, in the same transaction, so the old name
 * becomes free and the new one cannot be taken twice. The GROUP may be changed
 * — an expense filed under the wrong head is a real and common mistake — and
 * the entries already posted keep their stored `groupId`, which is deliberate:
 * a report for last August should show the classification that was in force
 * last August. `reclassifyEntries` below is the explicit opt-in for restating
 * history, and it is a separate act because it changes a published figure.
 */
export async function updateLedger({ businessId, ledgerId, values, current = null, actor = null }) {
  if (!businessId || !ledgerId) throw new ChartError('Missing ledger.', 'no-ledger');

  const patch = { updatedAt: serverTimestamp() };
  const renaming =
    values.name !== undefined && nameKey(values.name) !== nameKey(current?.name || '');

  const simple = [
    'gstin', 'pan', 'phone', 'email', 'bankName', 'branch', 'accountNumber', 'ifsc', 'taxRole',
  ];
  simple.forEach((field) => {
    if (values[field] !== undefined) {
      const value = String(values[field] || '').trim();
      patch[field] = value ? (['gstin', 'pan', 'ifsc'].includes(field) ? value.toUpperCase() : value) : null;
    }
  });
  if (values.address !== undefined) patch.address = values.address || null;
  if (values.notes !== undefined) patch.notes = String(values.notes || '').slice(0, 500);
  if (values.groupId !== undefined) patch.groupId = values.groupId;
  if (values.billwise !== undefined) patch.billwise = !!values.billwise;
  if (values.creditPeriodDays !== undefined) {
    patch.creditPeriodDays = Number.isFinite(Number(values.creditPeriodDays))
      ? Number(values.creditPeriodDays)
      : null;
  }
  if (values.creditLimit !== undefined) patch.creditLimit = round(values.creditLimit) || null;

  if (values.openingBalance !== undefined) {
    patch.openingBalance = Math.abs(round(values.openingBalance));
    patch.openingSide = values.openingSide === SIDES.CREDIT ? SIDES.CREDIT : SIDES.DEBIT;
  }

  if (!renaming) {
    if (values.name !== undefined) patch.name = String(values.name).trim();
    await updateDoc(ledgerDoc(businessId, ledgerId), patch);
  } else {
    const name = String(values.name).trim();
    const nextKey = nameKey(name);
    const previousKey = nameKey(current?.name || '');

    await runTransaction(db, async (tx) => {
      const taken = await tx.get(ledgerNameDoc(businessId, nextKey));
      if (taken.exists() && taken.data().ledgerId !== ledgerId) {
        throw new ChartError(`A ledger called “${name}” already exists.`, 'duplicate-name');
      }
      const now = serverTimestamp();
      tx.update(ledgerDoc(businessId, ledgerId), { ...patch, name, nameKey: nextKey });
      tx.set(ledgerNameDoc(businessId, nextKey), { ledgerId, updatedAt: now });
      if (previousKey && previousKey !== nextKey) {
        tx.delete(ledgerNameDoc(businessId, previousKey));
      }
    });
  }

  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.SETTINGS_CHANGED,
    subjectId: ledgerId,
    subjectLabel: values.name || current?.name || ledgerId,
    detail: 'Ledger changed',
  });

  return { id: ledgerId };
}

/**
 * Archive a ledger.
 *
 * Refused while it still has a balance. A hidden ledger with ₹40,000 in it is
 * ₹40,000 that no longer appears on any screen but still appears in the trial
 * balance total, and finding it is somebody's afternoon.
 */
export async function archiveLedger({ businessId, ledgerId, balance = 0, actor = null }) {
  if (round(balance) !== 0) {
    throw new ChartError(
      'This ledger still has a balance. Clear it to nil first — a hidden ledger with money in it is money nobody can find.',
      'has-balance'
    );
  }
  await updateDoc(ledgerDoc(businessId, ledgerId), {
    isArchived: true,
    updatedAt: serverTimestamp(),
  });
  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.SETTINGS_CHANGED,
    subjectId: ledgerId,
    detail: 'Ledger archived',
  });
  return { id: ledgerId };
}

export async function restoreLedger({ businessId, ledgerId }) {
  await updateDoc(ledgerDoc(businessId, ledgerId), {
    isArchived: false,
    updatedAt: serverTimestamp(),
  });
  return { id: ledgerId };
}

/**
 * Restate history after a ledger has been moved to a different group.
 *
 * Deliberately separate, deliberately explicit, and deliberately batched
 * outside a transaction: it can touch thousands of rows, which no transaction
 * will hold. It is safe to interrupt and re-run — each write is idempotent.
 */
export async function reclassifyEntries({ businessId, ledgerId, groupId, nature }) {
  const snap = await getDocs(
    query(accEntriesCol(businessId), where('ledgerId', '==', ledgerId), limit(2000))
  );

  let written = 0;
  let batch = writeBatch(db);
  for (const entry of snap.docs) {
    batch.update(entry.ref, { groupId, nature });
    written += 1;
    /* Firestore's hard cap. Commit and start a fresh batch rather than
       discovering it as a failed write at row 501. */
    if (written % 450 === 0) {
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
      batch = writeBatch(db);
    }
  }
  if (written % 450 !== 0) await batch.commit();

  return { written, more: snap.size === 2000 };
}

/* ------------------------------------------------------------------ *
 * Reading
 * ------------------------------------------------------------------ */

export function groupsQuery(businessId) {
  if (!businessId) return null;
  return query(groupsCol(businessId), orderBy('sortOrder', 'asc'));
}

export function ledgersQuery(businessId) {
  if (!businessId) return null;
  return query(ledgersCol(businessId), orderBy('name', 'asc'));
}

/** One ledger by name, for the migration and for imports. */
export async function findLedgerByName({ businessId, name }) {
  const snap = await getDocs(
    query(ledgersCol(businessId), where('nameKey', '==', nameKey(name)), limit(1))
  );
  if (snap.empty) return null;
  const found = snap.docs[0];
  return { id: found.id, ...found.data() };
}

/**
 * Get or create a ledger by name — what an import and the billing bridge need.
 *
 * Not exposed to the UI: a screen should show the user what is about to be
 * created, and a silent create is how a chart of accounts fills up with
 * "Electricity ", "electricity" and "Electricty".
 */
export async function ensureLedger({ businessId, name, groupId, extra = {} }) {
  const existing = await findLedgerByName({ businessId, name });
  if (existing) return existing;
  const created = await createLedger({
    businessId,
    values: { name, groupId, ...extra },
  });
  return { id: created.id, name, groupId, ...extra };
}

/**
 * Move a ledger's stored movement by a delta.
 *
 * Exported for the posting engine and for nothing else — a screen that adjusts
 * a balance directly is a screen that can put the books out without a voucher
 * behind it.
 */
export function ledgerMovementPatch({ signed, debit, credit }) {
  return {
    movement: increment(round(signed)),
    totalDebit: increment(round(debit)),
    totalCredit: increment(round(credit)),
    lastMovementAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
}

/** Free a name reservation. Used by the migration when de-duplicating. */
export async function releaseLedgerName({ businessId, name }) {
  await deleteDoc(ledgerNameDoc(businessId, nameKey(name)));
}
